import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import STLThumbnail from '../components/STLThumbnail';

function AdminPage({ locationFilter, setLocationFilter, setCurrentPage }) {
  const [mainTab, setMainTab] = useState('3d-printing');
  const [secondaryTab, setSecondaryTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [laserJobs, setLaserJobs] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [laserCutters, setLaserCutters] = useState([]);
  const [plastics, setPlastics] = useState([]);
  const [nozzles, setNozzles] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [modal, setModal] = useState(null);
  const [modalData, setModalData] = useState({});
  const [bambuStatus, setBambuStatus] = useState({});

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, printersRes, laserCuttersRes, plasticsRes, nozzlesRes, colorsRes, laserJobsRes] = await Promise.all([
        api.get('/admin/jobs'),
        api.get('/admin/printers'),
        api.get('/admin/laser-cutters'),
        api.get('/admin/plastics'),
        api.get('/admin/nozzles'),
        api.get('/admin/colors'),
        api.get('/admin/laser-jobs')
      ]);
      setJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
      setPrinters(Array.isArray(printersRes.data) ? printersRes.data : []);
      setLaserCutters(Array.isArray(laserCuttersRes.data) ? laserCuttersRes.data : []);
      setPlastics(Array.isArray(plasticsRes.data) ? plasticsRes.data : []);
      setNozzles(Array.isArray(nozzlesRes.data) ? nozzlesRes.data : []);
      setColors(Array.isArray(colorsRes.data) ? colorsRes.data : []);
      setLaserJobs(Array.isArray(laserJobsRes.data) ? laserJobsRes.data : []);
      setLoading(false);
      
      // Fetch Bambu Lab real-time data from VPS API (updated by gateway)
      try {
        const bambuRes = await fetch('/hub/api/printers/bambu/status', { timeout: 5000 });
        if (bambuRes.ok) {
          const data = await bambuRes.json();
          console.log('[AdminPage] Fetched Bambu data from VPS');
          setBambuStatus(data || {});
        }
      } catch (err) {
        console.log('[AdminPage] Failed to fetch Bambu data');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleDownloadSTL = async (jobId, firstName, lastName) => {
    try {
      const response = await api.get(`/admin/jobs/${jobId}/download`, {
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${firstName}_${lastName}_job_${jobId}.stl`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }
      
      // Create a blob link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  const handleDownloadSVG = async (jobId, firstName, lastName) => {
    try {
      const response = await api.get(`/admin/laser-jobs/${jobId}/download`, {
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${firstName}_${lastName}_job_${jobId}`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      } else if (response.headers['x-file-extension']) {
        // Fallback: use custom header for extension if Content-Disposition parsing failed
        fileName = `${firstName}_${lastName}_job_${jobId}${response.headers['x-file-extension']}`;
      }
      
      // Create a blob link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  const handleAssignJob = (jobId) => {
    setSelectedJob(jobId);
    setModal('assign');
    setModalData({ printerId: '' });
  };

  const handleMarkSliced = async (jobId) => {
    try {
      await api.put(`/admin/jobs/${jobId}/slice`);
      fetchData();
    } catch (err) {
      console.error('Error marking job as sliced:', err);
    }
  };

  const handleMarkDone = (jobId) => {
    setSelectedJob(jobId);
    setModal('done');
    setModalData({ pickupLocation: '' });
  };

  const handleLogError = (jobId) => {
    setSelectedJob(jobId);
    setModal('error');
    setModalData({ errorMessage: '', adminComments: '' });
  };

  const handleCleanupJobs = async () => {
    try {
      const response = await api.delete('/admin/jobs/cleanup');
      setModal(null);
      fetchData();
    } catch (err) {
      console.error('Error cleaning up jobs:', err);
    }
  };

  const handleCleanupLaserJobs = async () => {
    try {
      await api.delete('/admin/laser-jobs/cleanup');
      setModal(null);
      fetchData();
    } catch (err) {
      console.error('Error cleaning up laser jobs:', err);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await api.delete(`/admin/jobs/${jobId}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting job:', err);
    }
  };

  const submitAssign = async () => {
    if (!modalData.printerId) {
      return;
    }
    try {
      await api.put(`/admin/jobs/${selectedJob}/assign`, {
        printerId: modalData.printerId
      });
      setModal(null);
      fetchData();
    } catch (err) {
      console.error('Error assigning job:', err);
    }
  };

  const submitDone = async () => {
    try {
      await api.put(`/admin/jobs/${selectedJob}/done`, {
        pickupLocation: modalData.pickupLocation
      });
      setModal(null);
      fetchData();
    } catch (err) {
      console.error('Error completing job:', err);
    }
  };

  const submitError = async () => {
    if (!modalData.errorMessage) {
      return;
    }
    try {
      await api.put(`/admin/jobs/${selectedJob}/error`, {
        errorMessage: modalData.errorMessage,
        adminComments: modalData.adminComments
      });
      setModal(null);
      fetchData();
    } catch (err) {
      console.error('Error logging error:', err);
    }
  };

  const handleAddPrinter = async () => {
    if (!modalData.name) {
      return;
    }
    try {
      await api.post('/admin/printers', { 
        name: modalData.name, 
        location: modalData.location || '',
        bambu_serial: modalData.bambu_serial || null
      });
      setModal(null);
      setModalData({});
      fetchData();
    } catch (err) {
      console.error('Error adding printer:', err);
    }
  };

  const handleEditPrinter = (printer) => {
    setSelectedJob(printer.id);
    setModal('edit-printer');
    setModalData({ 
      name: printer.name, 
      location: printer.location || '',
      bambu_serial: printer.bambu_serial || '',
      nozzle_size: printer.nozzle_size != null ? printer.nozzle_size : 0.4,
      has_ams: printer.has_ams ? true : false,
      filament_type: printer.filament_type || 'petg',
      filament_color: printer.filament_color || '#1976D2',
      ams_slot_1_type: printer.ams_slot_1_type || 'petg',
      ams_slot_1_color: printer.ams_slot_1_color || '#F44336',
      ams_slot_2_type: printer.ams_slot_2_type || 'petg',
      ams_slot_2_color: printer.ams_slot_2_color || '#4CAF50',
      ams_slot_3_type: printer.ams_slot_3_type || 'petg',
      ams_slot_3_color: printer.ams_slot_3_color || '#FF9800',
      ams_slot_4_type: printer.ams_slot_4_type || 'petg',
      ams_slot_4_color: printer.ams_slot_4_color || '#9C27B0',
    });
  };

  const submitEditPrinter = async () => {
    if (!modalData.name) {
      return;
    }
    try {
      await api.put(`/admin/printers/${selectedJob}`, { 
        name: modalData.name, 
        location: modalData.location || '',
        bambu_serial: modalData.bambu_serial || null,
        nozzle_size: parseFloat(modalData.nozzle_size) || 0.4,
        has_ams: modalData.has_ams ? 1 : 0,
        filament_type: modalData.filament_type || 'petg',
        filament_color: modalData.filament_color || '#1976D2',
        ams_slot_1_type: modalData.ams_slot_1_type || 'petg',
        ams_slot_1_color: modalData.ams_slot_1_color || '#F44336',
        ams_slot_2_type: modalData.ams_slot_2_type || 'petg',
        ams_slot_2_color: modalData.ams_slot_2_color || '#4CAF50',
        ams_slot_3_type: modalData.ams_slot_3_type || 'petg',
        ams_slot_3_color: modalData.ams_slot_3_color || '#FF9800',
        ams_slot_4_type: modalData.ams_slot_4_type || 'petg',
        ams_slot_4_color: modalData.ams_slot_4_color || '#9C27B0',
      });
      setModal(null);
      setModalData({});
      fetchData();
    } catch (err) {
      console.error('Error updating printer:', err);
    }
  };

  const handleSetPrinterStatus = async (printerId, status) => {
    try {
      // Optimistic update - update local state immediately
      const updatedPrinters = printers.map(p => 
        p.id === printerId ? { ...p, status } : p
      );
      setPrinters(updatedPrinters);
      
      // Then make the API call
      await api.put(`/admin/printers/${printerId}/status`, { status });
      
      // Refresh data after a short delay to ensure server has processed it
      setTimeout(fetchData, 500);
    } catch (err) {
      console.error('Error updating printer status:', err);
      // Refetch on error to revert the optimistic update
      fetchData();
    }
  };

  const handleResetPrinterJob = async (printerId) => {
    try {
      await api.put(`/admin/printers/${printerId}/reset-job`);
      fetchData();
    } catch (err) {
      console.error('Error resetting printer job:', err);
    }
  };

  const getContrastColor = (hex) => {
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    // Perceived luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.55 ? '#000000' : '#ffffff';
  };

  const handleDeletePrinter = async (id) => {
    try {
      await api.delete(`/admin/printers/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting printer:', err);
    }
  };

  // ===== LASER CUTTER HANDLERS =====
  const handleEditLaserCutter = (cutter) => {
    setSelectedJob(cutter.id);
    setModal('edit-laser-cutter');
    setModalData({ name: cutter.name, location: cutter.location || '' });
  };

  const submitEditLaserCutter = async () => {
    if (!modalData.name) {
      return;
    }
    try {
      await api.put(`/admin/laser-cutters/${selectedJob}`, { 
        name: modalData.name, 
        location: modalData.location || '' 
      });
      setModal(null);
      setModalData({});
      fetchData();
    } catch (err) {
      console.error('Error updating laser cutter:', err);
    }
  };

  const handleSetLaserCutterStatus = async (cutterId, status) => {
    try {
      await api.put(`/admin/laser-cutters/${cutterId}/status`, { status });
      fetchData();
    } catch (err) {
      console.error('Error updating laser cutter status:', err);
    }
  };

  const handleDeleteLaserCutter = async (id) => {
    try {
      await api.delete(`/admin/laser-cutters/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting laser cutter:', err);
    }
  };

  const submitAddLaserCutter = async () => {
    if (!modalData.name) {
      alert('Laser cutter name required');
      return;
    }
    try {
      await api.post('/admin/laser-cutters', { 
        name: modalData.name,
        location: modalData.location || ''
      });
      setModal(null);
      setModalData({});
      fetchData();
    } catch (err) {
      console.error('Error adding laser cutter:', err);
      alert('Error adding laser cutter');
    }
  };

  const handleManagePlasticColors = (plastic) => {
    setSelectedJob(plastic.id);
    setModal('manage-plastic-colors');
    setModalData({ selectedPlasticId: plastic.id, selectedColors: [] });
    // Fetch existing colors for this plastic
    api.get(`/admin/plastics/${plastic.id}/colors`).then(res => {
      setModalData(prev => ({
        ...prev,
        selectedColors: res.data.map(c => c.id)
      }));
    });
  };

  const toggleColorSelection = (colorId) => {
    setModalData(prev => {
      const colors = prev.selectedColors || [];
      if (colors.includes(colorId)) {
        return {
          ...prev,
          selectedColors: colors.filter(id => id !== colorId)
        };
      } else {
        return {
          ...prev,
          selectedColors: [...colors, colorId]
        };
      }
    });
  };

  const submitPlasticColors = async () => {
    try {
      await api.put(`/admin/plastics/${modalData.selectedPlasticId}/colors`, {
        colorIds: modalData.selectedColors || []
      });
      setModal(null);
      setModalData({});
      fetchData();
    } catch (err) {
      console.error('Error updating plastic colors:', err);
    }
  };

  const handleAddPlastic = async () => {
    if (!modalData.name) {
      return;
    }
    try {
      await api.post('/admin/plastics', { name: modalData.name });
      setModal(null);
      setModalData({});
      fetchData();
    } catch (err) {
      console.error(err.response?.data?.error || 'Error adding plastic:', err);
    }
  };

  const handleDeletePlastic = async (id) => {
    try {
      await api.delete(`/admin/plastics/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting plastic:', err);
    }
  };

  const handleAddNozzle = async () => {
    if (!modalData.size) {
      return;
    }
    try {
      await api.post('/admin/nozzles', { size: modalData.size });
      setModal(null);
      setModalData({});
      fetchData();
    } catch (err) {
      console.error(err.response?.data?.error || 'Error adding nozzle:', err);
    }
  };

  const handleDeleteNozzle = async (id) => {
    try {
      await api.delete(`/admin/nozzles/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting nozzle:', err);
    }
  };

  const handleAddColor = async () => {
    if (!modalData.name) {
      return;
    }
    try {
      await api.post('/admin/colors', { name: modalData.name });
      setModal(null);
      setModalData({});
      fetchData();
    } catch (err) {
      console.error(err.response?.data?.error || 'Error adding color:', err);
    }
  };

  const handleDeleteColor = async (id) => {
    try {
      await api.delete(`/admin/colors/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting color:', err);
    }
  };

  const handleMarkLaserJobDone = (jobId) => {
    setSelectedJob(jobId);
    setModal('laser-done');
    setModalData({ pickupLocation: '' });
  };

  const submitMarkLaserJobDone = async () => {
    try {
      await api.put(`/admin/laser-jobs/${selectedJob}/done`, {
        pickupLocation: modalData.pickupLocation
      });
      setModal(null);
      fetchData();
    } catch (err) {
      console.error('Error marking laser job as done:', err);
    }
  };

  const handleDeleteLaserJob = async (jobId) => {
    try {
      await api.delete(`/admin/laser-jobs/${jobId}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting laser job:', err);
    }
  };

  const handleAssignLaserJob = (jobId) => {
    setSelectedJob(jobId);
    setModal('assign-laser-job');
    setModalData({ laserCutterId: '' });
  };

  const submitAssignLaserJob = async () => {
    if (!modalData.laserCutterId) {
      alert('Please select a laser cutter');
      return;
    }
    try {
      await api.put(`/admin/laser-jobs/${selectedJob}/assign`, {
        laserCutterId: modalData.laserCutterId
      });
      setModal(null);
      fetchData();
    } catch (err) {
      console.error('Error assigning laser job:', err);
    }
  };

  const handleErrorLaserJob = async () => {
    if (!modalData.errorMessage) {
      alert('Please enter an error message');
      return;
    }
    try {
      await api.put(`/admin/laser-jobs/${selectedJob}/error`, {
        errorMessage: modalData.errorMessage
      });
      setModal(null);
      fetchData();
    } catch (err) {
      console.error('Error marking laser job as error:', err);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="admin-page">
      <h2>Admin Dashboard</h2>

      {/* MAIN TABS - 3D PRINTING vs LASER CUTTING */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '20px',
        borderBottom: '3px solid #ddd'
      }}>
        <button 
          onClick={() => { setMainTab('3d-printing'); setSecondaryTab('jobs'); }}
          style={{
            backgroundColor: mainTab === '3d-printing' ? '#4CAF50' : '#f5f5f5',
            color: mainTab === '3d-printing' ? 'white' : '#333',
            border: 'none',
            padding: '15px 25px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            borderBottom: mainTab === '3d-printing' ? '4px solid #2196F3' : 'none',
            marginBottom: '-3px',
            transition: 'all 0.2s'
          }}
        >
          🖨️ 3D Printing
        </button>
        <button 
          onClick={() => { setMainTab('laser-cutting'); setSecondaryTab('jobs'); }}
          style={{
            backgroundColor: mainTab === 'laser-cutting' ? '#4CAF50' : '#f5f5f5',
            color: mainTab === 'laser-cutting' ? 'white' : '#333',
            border: 'none',
            padding: '15px 25px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            borderBottom: mainTab === 'laser-cutting' ? '4px solid #FF6B35' : 'none',
            marginBottom: '-3px',
            transition: 'all 0.2s'
          }}
        >
          🔪 Laser Cutting
        </button>
      </div>

      {/* 3D PRINTING JOBS & PRINTERS SECTION */}
      {mainTab === '3d-printing' && (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <h3>3D Print Jobs ({jobs.filter(j => j.status !== 'completed').length})</h3>
            <button 
              onClick={() => setCurrentPage('add-job')}
              style={{
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginRight: '10px',
                marginBottom: '15px'
              }}
            >
              + Add 3D Print Job
            </button>
            <button 
              onClick={() => {
                handleCleanupJobs();
              }}
              style={{
                backgroundColor: '#FF6B6B',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '15px'
              }}
            >
              Clean Up Done/Error Jobs
            </button>
          </div>
          {jobs.length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th></th>
                  <th>Details</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.filter(job => job.status !== 'completed').map(job => (
                  <tr key={job.id} style={job.print_for_user ? { backgroundColor: '#fffde7', borderLeft: '3px solid #f9a825' } : {}}>
                    <td>
                      {job.user_first_name} {job.user_last_name}
                      {job.print_for_user ? <span title="User requested staff print this" style={{ marginLeft: '6px', fontSize: '11px', backgroundColor: '#f9a825', color: '#fff', borderRadius: '3px', padding: '1px 5px', fontWeight: 'bold' }}>PRINT FOR USER</span> : null}
                    </td>
                    <td style={{ width: '60px', padding: '4px' }}>
                      <STLThumbnail filePath={`/jobs/${job.id}/preview`} width={56} height={42} />
                    </td>
                    <td style={{ fontSize: '12px', color: '#555' }}>
                      {job.notes ? job.notes.substring(0, 100) + (job.notes.length > 100 ? '...' : '') : '(no notes)'}
                    </td>
                    <td>
                      <span className={`status-badge ${job.status}`}>
                        {job.status}
                      </span>
                      {(job.status === 'printing' || job.status === 'printed') && job.printer_name && (
                        <div style={{ marginTop: '5px', fontSize: '12px', color: '#2196F3', fontWeight: 'bold' }}>
                          🖨️ {job.printer_name}
                        </div>
                      )}
                    </td>
                    <td>
                      <button 
                        onClick={() => handleDownloadSTL(job.id, job.user_first_name, job.user_last_name)}
                        style={{
                          backgroundColor: '#2196F3',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginRight: '5px',
                          fontSize: '12px'
                        }}
                      >
                        Download
                      </button>
                      {job.status === 'queued' && (
                        <>
                          <button 
                            onClick={() => handleMarkSliced(job.id)}
                            style={{
                              backgroundColor: '#FF9800',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginRight: '5px',
                              fontSize: '12px'
                            }}
                          >
                            Mark Sliced
                          </button>
                          <button onClick={() => handleAssignJob(job.id)}>Assign</button>
                        </>
                      )}
                      {job.status === 'sliced' && (
                        <button onClick={() => handleAssignJob(job.id)}>Assign</button>
                      )}
                      {job.status === 'printing' && (
                        <button onClick={() => handleMarkDone(job.id)}>Done</button>
                      )}
                      {job.status === 'printed' && (
                        <button onClick={() => handleMarkDone(job.id)}>Done</button>
                      )}
                      {job.status !== 'error' && (
                        <button 
                          className="error-btn"
                          onClick={() => handleLogError(job.id)}
                        >
                          Error
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteJob(job.id)}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          marginLeft: '5px'
                        }}
                        title="Delete job"
                      >
                        🗑️
                      </button>
                      {job.error_message && (
                        <div style={{ fontSize: '12px', color: '#f44336', marginTop: '5px' }}>
                          Error: {job.error_message}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: 'center', color: '#999' }}>No 3D print jobs</p>
          )}

          <hr style={{ margin: '30px 0' }} />

          <h3>Printers ({printers.length})</h3>
          <button 
            onClick={() => { setModal('add-printer'); setModalData({}); }}
            style={{
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '15px',
              marginRight: '10px'
            }}
          >
            + Add Printer
          </button>
          
          {/* Location Filter */}
          <div style={{ marginBottom: '15px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['All', 'Drake', 'Garth'].map(location => (
              <button
                key={location}
                onClick={() => setLocationFilter(location)}
                style={{
                  backgroundColor: locationFilter === location ? '#008CBA' : '#e0e0e0',
                  color: locationFilter === location ? 'white' : 'black',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: locationFilter === location ? 'bold' : 'normal'
                }}
              >
                {location}
              </button>
            ))}
          </div>
          
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th>Status</th>
                <th>Bambu Details</th>
                <th>Set Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {printers
                .filter(printer => locationFilter === 'All' || printer.location === locationFilter)
                .map(printer => {
                  const bambuData = printer.bambu_serial ? bambuStatus[printer.bambu_serial] : null;
                  const systemStatus = printer.status;
                  const printerStatus = printer.printer_real_status || printer.status;
                  const hasOverride = printer.manual_status_override && systemStatus !== printerStatus;
                  return (
                  <tr key={printer.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span>{printer.name}</span>
                        {printer.nozzle_size != null && parseFloat(printer.nozzle_size) !== 0.4 && (
                          <span
                            onClick={() => handleEditPrinter(printer)}
                            title="Non-standard nozzle — click to edit"
                            style={{
                              display: 'inline-flex', alignItems: 'center',
                              backgroundColor: '#212121', color: '#fff',
                              padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                              fontWeight: '800', cursor: 'pointer', userSelect: 'none',
                              letterSpacing: '0.3px', boxShadow: '0 1px 4px rgba(0,0,0,0.35)'
                            }}
                          >
                            {parseFloat(printer.nozzle_size)} mm
                          </span>
                        )}
                        {printer.has_ams ? (
                          [1, 2, 3, 4].map(slot => {
                            const type = printer[`ams_slot_${slot}_type`];
                            const color = printer[`ams_slot_${slot}_color`] || '#999';
                            if (!type) return null;
                            if (type === 'empty') return (
                              <span key={slot} onClick={() => handleEditPrinter(printer)} title={`AMS Slot ${slot} — click to edit`} style={{ cursor: 'pointer', color: '#999' }}>-</span>
                            );
                            return (
                              <span
                                key={slot}
                                onClick={() => handleEditPrinter(printer)}
                                title={`AMS Slot ${slot} — click to edit`}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                                  backgroundColor: color, color: getContrastColor(color),
                                  padding: '1px 6px', borderRadius: '12px', fontSize: '10px',
                                  fontWeight: 'bold', cursor: 'pointer', userSelect: 'none',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }}
                              >
                                {type.toUpperCase()}
                              </span>
                            );
                          })
                        ) : (
                          printer.filament_type && (
                            printer.filament_type === 'empty' ? (
                              <span onClick={() => handleEditPrinter(printer)} title="Click to edit" style={{ cursor: 'pointer', color: '#999' }}>-</span>
                            ) : (
                            <span
                              onClick={() => handleEditPrinter(printer)}
                              title="Click to edit"
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                backgroundColor: printer.filament_color || '#1976D2',
                                color: getContrastColor(printer.filament_color || '#1976D2'),
                                padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
                                fontWeight: 'bold', cursor: 'pointer', userSelect: 'none',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}
                            >
                              {printer.filament_type.toUpperCase()}
                            </span>
                            )
                          )
                        )}
                      </div>
                    </td>
                    <td>{printer.location || '-'}</td>
                    <td>
                      {hasOverride ? (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span className={`status-badge ${printerStatus}`}>{printerStatus}</span>
                          <span style={{ fontSize: '11px', backgroundColor: '#fff3cd', color: '#856404', padding: '3px 6px', borderRadius: '3px', fontWeight: 'bold' }}>Admin: {systemStatus}</span>
                        </div>
                      ) : (
                        <span className={`status-badge ${printerStatus}`}>{printerStatus}</span>
                      )}
                    </td>
                    <td style={{ fontSize: '12px', maxWidth: '200px' }}>
                      {bambuData ? (
                        <div>
                          <div>🌡️ {Math.round(bambuData.nozzleTemp)}°C / {Math.round(bambuData.bedTemp)}°C</div>
                          <div>📊 {Math.round(bambuData.progress || 0)}% - {bambuData.remainingTime ? `${bambuData.remainingTime} min` : 'N/A'}</div>
                          {bambuData.filename && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>📁 {bambuData.filename.substring(0, 20)}</div>}
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button 
                      onClick={() => handleSetPrinterStatus(printer.id, 'idle')}
                      style={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '3px',
                        fontSize: '11px'
                      }}
                    >
                      Idle
                    </button>
                    <button 
                      onClick={() => handleSetPrinterStatus(printer.id, 'printing')}
                      style={{
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '3px',
                        fontSize: '11px'
                      }}
                    >
                      Printing
                    </button>
                    <button 
                      onClick={() => handleSetPrinterStatus(printer.id, 'maintenance')}
                      style={{
                        backgroundColor: '#9C27B0',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                    >
                      Maintenance
                    </button>
                  </td>
                  <td>
                    <button 
                      onClick={() => handleEditPrinter(printer)}
                      style={{
                        backgroundColor: '#FF9800',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '5px'
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleResetPrinterJob(printer.id)}
                      style={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '5px'
                      }}
                    >
                      Reset Job
                    </button>
                  </td>
                  </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* COMPLETED JOBS SECTION */}
      {mainTab === '3d-printing' && (
        <div>
          <hr style={{ margin: '30px 0' }} />
          <h3>Completed Jobs ({jobs.filter(j => j.status === 'completed').length})</h3>
          {jobs.filter(j => j.status === 'completed').length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Details</th>
                  <th>Pickup Location</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.filter(job => job.status === 'completed').map(job => (
                  <tr key={job.id}>
                    <td>{job.user_first_name} {job.user_last_name}</td>
                    <td style={{ fontSize: '12px', color: '#555' }}>
                      {job.notes ? job.notes.substring(0, 100) + (job.notes.length > 100 ? '...' : '') : '(no notes)'}
                    </td>
                    <td>{job.pickup_location || '-'}</td>
                    <td>
                      <button 
                        onClick={() => handleDeleteJob(job.id)}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: 'center', color: '#999' }}>No completed jobs</p>
          )}
        </div>
      )}

      {/* LASER CUTTING JOBS & CUTTERS SECTION */}
      {mainTab === 'laser-cutting' && (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <h3>Laser Jobs ({laserJobs.filter(j => j.status !== 'completed').length})</h3>
            <button 
              onClick={() => setCurrentPage('add-job')}
              style={{
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginRight: '10px',
                marginBottom: '15px'
              }}
            >
              + Add Laser/Other Job
            </button>
            <button 
              onClick={() => {
                if (window.confirm('Delete all completed and error laser jobs?')) {
                  handleCleanupLaserJobs();
                }
              }}
              style={{
                backgroundColor: '#FF6B6B',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '15px'
              }}
            >
              Clean Up Done/Error Laser Jobs
            </button>
          </div>

          {laserJobs.filter(j => j.status !== 'completed').length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Notes</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {laserJobs.filter(job => job.status !== 'completed').map(job => (
                  <tr key={job.id} style={job.print_for_user ? { backgroundColor: '#fffde7', borderLeft: '3px solid #f9a825' } : {}}>
                    <td>
                      {job.user_first_name} {job.user_last_name}
                      {job.print_for_user ? <span title="User requested staff cut this" style={{ marginLeft: '6px', fontSize: '11px', backgroundColor: '#f9a825', color: '#fff', borderRadius: '3px', padding: '1px 5px', fontWeight: 'bold' }}>CUT FOR USER</span> : null}
                    </td>
                    <td style={{ maxWidth: '200px', whiteSpace: 'normal' }}>{job.notes || '-'}</td>
                    <td>
                      <span className={`status-badge ${job.status}`}>
                        {job.status}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleDownloadSVG(job.id, job.user_first_name, job.user_last_name)}
                        style={{
                          backgroundColor: '#2196F3',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginRight: '5px',
                          fontSize: '12px'
                        }}
                        title="Download SVG file"
                      >
                        Download
                      </button>
                      {job.status === 'queued' && (
                        <button 
                          onClick={() => {
                            setSelectedJob(job.id);
                            setModal('assign-laser-job');
                            setModalData({ laserCutterId: '' });
                          }}
                          style={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '5px',
                            fontSize: '12px'
                          }}
                        >
                          Assign
                        </button>
                      )}
                      {job.status === 'cutting' && (
                        <button 
                          onClick={() => handleMarkLaserJobDone(job.id)}
                          style={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '5px',
                            fontSize: '12px'
                          }}
                        >
                          Done
                        </button>
                      )}
                      {job.status !== 'error' && (
                        <button 
                          className="error-btn"
                          onClick={() => {
                            setSelectedJob(job.id);
                            setModal('error-laser-job');
                            setModalData({ errorMessage: job.error_message || '' });
                          }}
                        >
                          Error
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteLaserJob(job.id)}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          marginLeft: '5px'
                        }}
                        title="Delete job"
                      >
                        🗑️
                      </button>
                      {job.error_message && (
                        <div style={{ fontSize: '12px', color: '#f44336', marginTop: '5px' }}>
                          Error: {job.error_message}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: 'center', color: '#999' }}>No active laser jobs</p>
          )}

          <hr style={{ margin: '30px 0' }} />
          <h3>Completed Laser Jobs ({laserJobs.filter(j => j.status === 'completed').length})</h3>
          {laserJobs.filter(j => j.status === 'completed').length > 0 ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Notes</th>
                  <th>Pickup Location</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {laserJobs.filter(job => job.status === 'completed').map(job => (
                  <tr key={job.id}>
                    <td>{job.user_first_name} {job.user_last_name}</td>
                    <td style={{ maxWidth: '200px', whiteSpace: 'normal' }}>{job.notes || '-'}</td>
                    <td>{job.pickup_location || '-'}</td>
                    <td>
                      <button 
                        onClick={() => handleDeleteLaserJob(job.id)}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: 'center', color: '#999' }}>No completed laser jobs</p>
          )}

          <hr style={{ margin: '30px 0' }} />

          <button 
            onClick={() => { setModal('add-laser-cutter'); setModalData({}); }}
            style={{
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '15px',
              marginRight: '10px'
            }}
          >
            + Add Laser Cutter
          </button>
          
          {/* Location Filter */}
          <div style={{ marginBottom: '15px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['All', 'Drake', 'Garth'].map(location => (
              <button
                key={location}
                onClick={() => setLocationFilter(location)}
                style={{
                  backgroundColor: locationFilter === location ? '#008CBA' : '#e0e0e0',
                  color: locationFilter === location ? 'white' : 'black',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: locationFilter === location ? 'bold' : 'normal'
                }}
              >
                {location}
              </button>
            ))}
          </div>
          
          <h3>Laser Cutters</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th>Status</th>
                <th>Set Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {laserCutters
                .filter(cutter => locationFilter === 'All' || cutter.location === locationFilter)
                .map(cutter => (
                <tr key={cutter.id}>
                  <td>{cutter.name}</td>
                  <td>{cutter.location || '-'}</td>
                  <td><span className={`status-badge ${cutter.status}`}>{cutter.status}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button 
                      onClick={() => handleSetLaserCutterStatus(cutter.id, 'idle')}
                      style={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '3px',
                        fontSize: '11px'
                      }}
                    >
                      Idle
                    </button>
                    <button 
                      onClick={() => handleSetLaserCutterStatus(cutter.id, 'cutting')}
                      style={{
                        backgroundColor: '#9C27B0',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '3px',
                        fontSize: '11px'
                      }}
                    >
                      Cutting
                    </button>
                    <button 
                      onClick={() => handleSetLaserCutterStatus(cutter.id, 'maintenance')}
                      style={{
                        backgroundColor: '#FF9800',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                    >
                      Maintenance
                    </button>
                  </td>
                  <td>
                    <button 
                      onClick={() => handleEditLaserCutter(cutter)}
                      style={{
                        backgroundColor: '#FF9800',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '5px'
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      className="error-btn"
                      onClick={() => handleDeleteLaserCutter(cutter.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign Modal */}
      <div className={`modal ${modal === 'assign' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Assign Job to Printer</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Select Printer:</label>
            <select 
              value={modalData.printerId}
              onChange={(e) => setModalData({...modalData, printerId: e.target.value})}
            >
              <option value="">-- Select Printer --</option>
              {printers.map(printer => {
                const bambuData = printer.bambu_serial ? bambuStatus[printer.bambu_serial] : null;
                const systemStatus = printer.status;
                const printerStatus = printer.printer_real_status || printer.status;
                const hasOverride = printer.manual_status_override && systemStatus !== printerStatus;
                const statusDisplay = hasOverride ? `${printerStatus} (Admin: ${systemStatus})` : printerStatus;
                return (
                <option key={printer.id} value={printer.id}>
                  {printer.name} ({statusDisplay})
                </option>
                );
              })}
            </select>
          </div>
          <button onClick={submitAssign} className="submit-btn">Assign</button>
        </div>
      </div>

      {/* Done Modal */}
      <div className={`modal ${modal === 'done' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Mark Job Complete</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Pickup Location (optional):</label>
            <input 
              type="text"
              value={modalData.pickupLocation}
              onChange={(e) => setModalData({...modalData, pickupLocation: e.target.value})}
              onKeyDown={(e) => e.key === 'Enter' && submitDone()}
              placeholder="e.g., Bin A, Shelf 3, etc."
              autoFocus
            />
          </div>
          <button onClick={submitDone} className="submit-btn">Complete Job</button>
        </div>
      </div>

      {/* Error Modal */}
      <div className={`modal ${modal === 'error' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Log Error</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Error Description:</label>
            <textarea 
              value={modalData.errorMessage}
              onChange={(e) => setModalData({...modalData, errorMessage: e.target.value})}
              placeholder="What went wrong?"
            />
          </div>
          <div className="form-group">
            <label>Admin Comments (visible to user):</label>
            <textarea 
              value={modalData.adminComments}
              onChange={(e) => setModalData({...modalData, adminComments: e.target.value})}
              placeholder="Optional comments to share with user"
            />
          </div>
          <button onClick={submitError} className="submit-btn">Log Error</button>
        </div>
      </div>

      {/* Add Printer Modal */}
      <div className={`modal ${modal === 'add-printer' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Add Printer</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Printer Name:</label>
            <input 
              type="text"
              value={modalData.name || ''}
              onChange={(e) => setModalData({...modalData, name: e.target.value})}
              placeholder="e.g., Printer A, Prusa i3, etc."
            />
          </div>
          <div className="form-group">
            <label>Location:</label>
            <select 
              value={modalData.location || ''}
              onChange={(e) => setModalData({...modalData, location: e.target.value})}
            >
              <option value="">-- Select Location --</option>
              <option value="Drake">Drake</option>
              <option value="Garth">Garth</option>
            </select>
          </div>
          <div className="form-group">
            <label>Bambu Lab Serial (optional):</label>
            <input 
              type="text"
              value={modalData.bambu_serial || ''}
              onChange={(e) => setModalData({...modalData, bambu_serial: e.target.value})}
              placeholder="e.g., 01P00A452301256"
            />
            <small style={{ color: '#999', marginTop: '4px', display: 'block' }}>Leave blank if not a Bambu Lab printer</small>
          </div>
          <button onClick={handleAddPrinter} className="submit-btn">Add Printer</button>
        </div>
      </div>

      {/* Add Plastic Modal */}
      <div className={`modal ${modal === 'add-plastic' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Add Plastic Type</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Plastic Name:</label>
            <input 
              type="text"
              value={modalData.name || ''}
              onChange={(e) => setModalData({...modalData, name: e.target.value})}
              placeholder="e.g., Nylon, Carbon Fiber, etc."
            />
          </div>
          <button onClick={handleAddPlastic} className="submit-btn">Add Plastic</button>
        </div>
      </div>

      {/* Add Nozzle Modal */}
      <div className={`modal ${modal === 'add-nozzle' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Add Nozzle Size</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Nozzle Size (mm):</label>
            <input 
              type="text"
              value={modalData.size || ''}
              onChange={(e) => setModalData({...modalData, size: e.target.value})}
              placeholder="e.g., 0.2, 1.2, etc."
            />
          </div>
          <button onClick={handleAddNozzle} className="submit-btn">Add Nozzle</button>
        </div>
      </div>

      {/* Add Color Modal */}
      <div className={`modal ${modal === 'add-color' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Add Color</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Color Name:</label>
            <input 
              type="text"
              value={modalData.name || ''}
              onChange={(e) => setModalData({...modalData, name: e.target.value})}
              placeholder="e.g., Orange, Pink, etc."
            />
          </div>
          <button onClick={handleAddColor} className="submit-btn">Add Color</button>
        </div>
      </div>

      {/* Edit Printer Modal */}
      <div className={`modal ${modal === 'edit-printer' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Edit Printer</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Printer Name:</label>
            <input 
              type="text"
              value={modalData.name || ''}
              onChange={(e) => setModalData({...modalData, name: e.target.value})}
              placeholder="e.g., Printer A, Prusa i3, etc."
            />
          </div>
          <div className="form-group">
            <label>Location:</label>
            <select 
              value={modalData.location || ''}
              onChange={(e) => setModalData({...modalData, location: e.target.value})}
            >
              <option value="">-- Select Location --</option>
              <option value="Drake">Drake</option>
              <option value="Garth">Garth</option>
            </select>
          </div>
          <div className="form-group">
            <label>Bambu Lab Serial (optional):</label>
            <input 
              type="text"
              value={modalData.bambu_serial || ''}
              onChange={(e) => setModalData({...modalData, bambu_serial: e.target.value})}
              placeholder="e.g., 01P00A452301256"
            />
            <small style={{ color: '#999', marginTop: '4px', display: 'block' }}>Leave blank if not a Bambu Lab printer</small>
          </div>
          <div className="form-group">
            <label>Nozzle Size:</label>
            <select
              value={modalData.nozzle_size != null ? modalData.nozzle_size : 0.4}
              onChange={(e) => setModalData({...modalData, nozzle_size: parseFloat(e.target.value)})}
            >
              <option value={0.2}>0.2 mm</option>
              <option value={0.4}>0.4 mm</option>
              <option value={0.6}>0.6 mm</option>
              <option value={0.8}>0.8 mm</option>
            </select>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!modalData.has_ams}
                onChange={(e) => setModalData({...modalData, has_ams: e.target.checked})}
                style={{ width: '18px', height: '18px' }}
              />
              Has AMS (Automatic Material System)
            </label>
          </div>

          {!modalData.has_ams && (
            <div style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
              <strong style={{ display: 'block', marginBottom: '10px' }}>Filament</strong>
              <div className="form-group">
                <label>Type:</label>
                <select
                  value={modalData.filament_type || 'petg'}
                  onChange={(e) => setModalData({...modalData, filament_type: e.target.value})}
                >
                  <option value="pla">PLA</option>
                  <option value="petg">PETG</option>
                  <option value="other">Other</option>
                  <option value="empty">Empty</option>
                </select>
              </div>
              {modalData.filament_type !== 'empty' && (
              <div className="form-group">
                <label>Color:</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="color"
                    value={modalData.filament_color || '#1976D2'}
                    onChange={(e) => setModalData({...modalData, filament_color: e.target.value})}
                    style={{ width: '48px', height: '36px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '13px', color: '#555' }}>{modalData.filament_color || '#1976D2'}</span>
                </div>
              </div>
              )}
            </div>
          )}

          {modalData.has_ams && (
            <div style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
              <strong style={{ display: 'block', marginBottom: '10px' }}>AMS Slots</strong>
              {[1, 2, 3, 4].map(slot => (
                <div key={slot} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <span style={{ minWidth: '50px', fontWeight: 'bold', fontSize: '13px' }}>Slot {slot}</span>
                  <select
                    value={modalData[`ams_slot_${slot}_type`] || 'petg'}
                    onChange={(e) => setModalData({...modalData, [`ams_slot_${slot}_type`]: e.target.value})}
                    style={{ flex: 1 }}
                  >
                    <option value="pla">PLA</option>
                    <option value="petg">PETG</option>
                    <option value="other">Other</option>
                    <option value="empty">Empty</option>
                  </select>
                  {modalData[`ams_slot_${slot}_type`] !== 'empty' && (
                  <input
                    type="color"
                    value={modalData[`ams_slot_${slot}_color`] || '#1976D2'}
                    onChange={(e) => setModalData({...modalData, [`ams_slot_${slot}_color`]: e.target.value})}
                    style={{ width: '48px', height: '36px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                  />
                  )}
                  {modalData[`ams_slot_${slot}_type`] !== 'empty' && (
                  <span style={{ fontSize: '12px', color: '#555', minWidth: '60px' }}>{modalData[`ams_slot_${slot}_color`] || '#1976D2'}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <button onClick={submitEditPrinter} className="submit-btn">Update Printer</button>
        </div>
      </div>

      {/* Add Laser Cutter Modal */}
      <div className={`modal ${modal === 'add-laser-cutter' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Add Laser Cutter</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Laser Cutter Name:</label>
            <input 
              type="text"
              value={modalData.name || ''}
              onChange={(e) => setModalData({...modalData, name: e.target.value})}
              placeholder="e.g., Laser 1, Epilog Zing, etc."
            />
          </div>
          <div className="form-group">
            <label>Location:</label>
            <select 
              value={modalData.location || ''}
              onChange={(e) => setModalData({...modalData, location: e.target.value})}
            >
              <option value="">-- Select Location --</option>
              <option value="Drake">Drake</option>
              <option value="Garth">Garth</option>
            </select>
          </div>
          <button onClick={submitAddLaserCutter} className="submit-btn">Add Laser Cutter</button>
        </div>
      </div>

      {/* Edit Laser Cutter Modal */}
      <div className={`modal ${modal === 'edit-laser-cutter' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Edit Laser Cutter</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Laser Cutter Name:</label>
            <input 
              type="text"
              value={modalData.name || ''}
              onChange={(e) => setModalData({...modalData, name: e.target.value})}
              placeholder="e.g., Laser 1, Epilog Zing, etc."
            />
          </div>
          <div className="form-group">
            <label>Location:</label>
            <select 
              value={modalData.location || ''}
              onChange={(e) => setModalData({...modalData, location: e.target.value})}
            >
              <option value="">-- Select Location --</option>
              <option value="Drake">Drake</option>
              <option value="Garth">Garth</option>
            </select>
          </div>
          <button onClick={submitEditLaserCutter} className="submit-btn">Update Laser Cutter</button>
        </div>
      </div>

      {/* Manage Plastic Colors Modal */}
      <div className={`modal ${modal === 'manage-plastic-colors' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Manage Colors for Plastic</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Select available colors:</label>
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
              {colors.map(color => (
                <label key={color.id} style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={(modalData.selectedColors || []).includes(color.id)}
                    onChange={() => toggleColorSelection(color.id)}
                    style={{ marginRight: '8px' }}
                  />
                  {color.name}
                </label>
              ))}
            </div>
          </div>
          <button onClick={submitPlasticColors} className="submit-btn">Save Colors</button>
        </div>
      </div>

      {/* Laser Job Done Modal */}
      <div className={`modal ${modal === 'laser-done' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Mark Laser Job Complete</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Pickup Location (optional):</label>
            <input 
              type="text"
              value={modalData.pickupLocation}
              onChange={(e) => setModalData({...modalData, pickupLocation: e.target.value})}
              onKeyDown={(e) => e.key === 'Enter' && submitMarkLaserJobDone()}
              placeholder="e.g., Bin A, Shelf 3, etc."
              autoFocus
            />
          </div>
          <button onClick={submitMarkLaserJobDone} className="submit-btn">Complete Job</button>
        </div>
      </div>

      {/* Assign Laser Job Modal */}
      <div className={`modal ${modal === 'assign-laser-job' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Assign Laser Job</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Laser Cutter:</label>
            <select 
              value={modalData.laserCutterId}
              onChange={(e) => setModalData({...modalData, laserCutterId: e.target.value})}
            >
              <option value="">Select a laser cutter</option>
              {laserCutters.map(cutter => (
                <option key={cutter.id} value={cutter.id}>
                  {cutter.name} ({cutter.status})
                </option>
              ))}
            </select>
          </div>
          <button onClick={submitAssignLaserJob} className="submit-btn">Assign Job</button>
        </div>
      </div>

      {/* Error Laser Job Modal */}
      <div className={`modal ${modal === 'error-laser-job' ? 'show' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Mark Laser Job as Error</h2>
            <button className="close-btn" onClick={() => setModal(null)}>×</button>
          </div>
          <div className="form-group">
            <label>Error Message:</label>
            <textarea
              value={modalData.errorMessage}
              onChange={(e) => setModalData({...modalData, errorMessage: e.target.value})}
              placeholder="Describe the error..."
              rows="4"
            />
          </div>
          <button onClick={handleErrorLaserJob} className="submit-btn">Mark as Error</button>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
